'use client'

import { createContext, useContext, useEffect, useRef, useState } from 'react'
import Cookies from 'js-cookie'
import logger from '@achieve/cx-logger'

const SESSION_CACHE_KEY = 'acx-pzn-section-eligible'
const COOKIE_POLL_INTERVAL_MS = 500
const COOKIE_POLL_MAX_ATTEMPTS = 20

const PersonalizationContext = createContext({ eligible: null })

const publishEligibility = (value) => {
  if (typeof window !== 'undefined') {
    window.__acxPznEligible = value
  }
}

export function PersonalizationProvider({ children }) {
  const [eligible, setEligible] = useState(null)
  const eligibilityPromiseRef = useRef(null)

  useEffect(() => {
    publishEligibility(false)

    const searchParams = new URLSearchParams(window.location.search)
    const eligibilityOverride = searchParams.get('pzn_eligibility')
    if (eligibilityOverride === 'true' || eligibilityOverride === 'false') {
      const result = eligibilityOverride === 'true'
      publishEligibility(result)
      setEligible(result)
      return () => publishEligibility(false)
    }

    const launchDarklyOverride = searchParams.get('utm_source')
    if (launchDarklyOverride?.toLowerCase() === 'control') {
      publishEligibility(false)
      setEligible(false)
      return () => publishEligibility(false)
    }

    if (/^variation_[a-z]$/i.test(launchDarklyOverride ?? '')) {
      publishEligibility(true)
      setEligible(true)
      return () => publishEligibility(false)
    }

    const cached = sessionStorage.getItem(SESSION_CACHE_KEY)
    if (cached === 'true' || cached === 'false') {
      const result = cached === 'true'
      publishEligibility(result)
      setEligible(result)
      return () => publishEligibility(false)
    }

    let cancelled = false
    let intervalId

    const fetchEligibility = (browserId, visitId) => {
      if (!eligibilityPromiseRef.current) {
        eligibilityPromiseRef.current = (async () => {
          try {
            const response = await fetch('/api/personalization', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                browserId,
                visitId,
                pathname: window.location.pathname,
              }),
            })

            if (!response.ok) throw new Error(`HTTP ${response.status}`)

            const result = await response.json()
            if (typeof result?.eligible !== 'boolean') {
              throw new Error('Invalid personalization response')
            }

            sessionStorage.setItem(SESSION_CACHE_KEY, String(result.eligible))
            return result.eligible
          } catch (error) {
            logger.error('[PersonalizationProvider] Eligibility fetch failed', { error })
            sessionStorage.setItem(SESSION_CACHE_KEY, 'false')
            return false
          }
        })()
      }

      return eligibilityPromiseRef.current
    }

    const updateEligibility = async (browserId, visitId) => {
      const result = await fetchEligibility(browserId, visitId)
      if (!cancelled) {
        publishEligibility(result)
        setEligible(result)
      }
    }

    const browserId = Cookies.get('browser_id')
    const visitId = Cookies.get('eh_visit_id')
    if (browserId && visitId) {
      void updateEligibility(browserId, visitId)
      return () => {
        cancelled = true
        publishEligibility(false)
      }
    }

    let attempts = 0
    intervalId = setInterval(() => {
      attempts++
      const currentBrowserId = Cookies.get('browser_id')
      const currentVisitId = Cookies.get('eh_visit_id')

      if (currentBrowserId && currentVisitId) {
        clearInterval(intervalId)
        void updateEligibility(currentBrowserId, currentVisitId)
      } else if (attempts >= COOKIE_POLL_MAX_ATTEMPTS) {
        clearInterval(intervalId)
        sessionStorage.setItem(SESSION_CACHE_KEY, 'false')
        publishEligibility(false)
        setEligible(false)
      }
    }, COOKIE_POLL_INTERVAL_MS)

    return () => {
      cancelled = true
      clearInterval(intervalId)
      publishEligibility(false)
    }
  }, [])

  return (
    <PersonalizationContext.Provider value={{ eligible }}>
      {children}
    </PersonalizationContext.Provider>
  )
}

export function usePersonalization() {
  return useContext(PersonalizationContext)
}
